import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building, Plus, Users as UsersIcon, Settings, Clock, Rocket, Zap, Crown, CreditCard, Trash2, AlertTriangle } from "lucide-react";

const PLAN_ICONS = { starter: Rocket, growth: Zap, enterprise: Crown };

function StatusBadge({ tenant }) {
  const s = tenant.billing_status || "trialing";
  const comped = tenant.is_billable === false;
  const map = {
    trialing: { bg: "hsl(38 92% 90%)", fg: "hsl(38 92% 35%)", label: "Trial" },
    active: { bg: "hsl(158 74% 92%)", fg: "hsl(158 74% 30%)", label: "Active" },
    past_due: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)", label: "Past due" },
    locked: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)", label: "Locked" },
    cancelled: { bg: "hsl(220 15% 90%)", fg: "hsl(220 30% 35%)", label: "Cancelled" },
  };
  const style = comped ? { bg: "hsl(227 44% 90%)", fg: "hsl(227 44% 30%)", label: "Comped" } : (map[s] || map.trialing);
  return <span className="text-[0.68rem] font-semibold px-2.5 py-1 rounded-full" style={{ background: style.bg, color: style.fg }}>{style.label}</span>;
}

export default function Tenants() {
  const qc = useQueryClient();
  const { data: tenants } = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => (await api.get("/tenants")).data,
    refetchInterval: 30000,
  });
  const { data: plans } = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => (await api.get("/billing/plans")).data,
  });
  const { data: platform } = useQuery({
    queryKey: ["platform-settings"],
    queryFn: async () => (await api.get("/platform/settings")).data,
  });

  const toggleGlobalBilling = async (v) => {
    try {
      await api.patch("/platform/settings", { billing_enabled: v });
      toast.success(v ? "Billing enabled platform-wide" : "Billing disabled platform-wide");
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const { data: demoSummary } = useQuery({
    queryKey: ["demo-summary"],
    queryFn: async () => (await api.get("/platform/demo-data")).data,
  });
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wiping, setWiping] = useState(false);

  const wipeDemo = async () => {
    if (wipeConfirm !== "WIPE") return;
    setWiping(true);
    try {
      const r = await api.post("/platform/wipe-demo", { confirm: "WIPE" });
      const n = r.data?.deleted?.tenants || 0;
      toast.success(n ? `${n} demo tenant${n !== 1 ? "s" : ""} wiped` : "No demo data to remove");
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["demo-summary"] });
      setWipeOpen(false);
      setWipeConfirm("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWiping(false); }
  };

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", industry: "Hospitality", contact_email: "",
    admin_email: "", admin_name: "", admin_password: "",
    plan: "starter", is_billable: false,
  });

  const submit = async () => {
    try {
      await api.post("/tenants", form);
      toast.success(`${form.name} onboarded`);
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setOpen(false);
      setForm({ name: "", industry: "Hospitality", contact_email: "", admin_email: "", admin_name: "", admin_password: "", plan: "starter", is_billable: false });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/billing/tenants/${editing.id}`, {
        plan: editing.plan,
        is_billable: editing.is_billable,
      });
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const extendTrial = async (t, days = 14) => {
    try {
      await api.post(`/billing/tenants/${t.id}/extend-trial`, { days });
      toast.success(`Trial extended ${days} days`);
      qc.invalidateQueries({ queryKey: ["tenants"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Platform</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Tenants</h1>
          <p className="text-sm text-muted-foreground mt-2">Onboard customers, pick their plan, and manage billing.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-tenant-btn" className="rounded-xl"><Plus className="w-4 h-4 mr-1" />New Tenant</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Onboard tenant</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Company name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" /></div>
              <div><Label>Industry</Label>
                <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
              <div><Label>Contact email</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><Label>Starting plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="tenant-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(plans || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.price_display}/mo</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col justify-end">
                <Label className="flex items-center justify-between">
                  <span>Billable?</span>
                  <Switch checked={form.is_billable} onCheckedChange={(v) => setForm({ ...form, is_billable: v })} data-testid="tenant-billable" />
                </Label>
                <p className="text-xs text-muted-foreground mt-1">Off = comped</p>
              </div>
              <div className="col-span-2 mt-2 pt-2 border-t border-border label-caps text-muted-foreground">Initial company admin</div>
              <div><Label>Admin name</Label>
                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} /></div>
              <div><Label>Admin email</Label>
                <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="tenant-admin-email" /></div>
              <div className="col-span-2"><Label>Admin password</Label>
                <Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="tenant-admin-password" /></div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="tenant-submit" className="rounded-xl">Create tenant</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Global billing feature flag */}
      <div
        className="mb-6 border border-border rounded-2xl bg-card p-5 flex items-center justify-between gap-4 flex-wrap"
        data-testid="billing-flag-card"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${platform?.billing_enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-base">Subscription billing</div>
            <div className="text-xs text-muted-foreground mt-1 max-w-xl">
              Master switch for the entire billing module. When OFF, the Billing page, trial banners and plan limits are hidden from every tenant — regardless of individual settings below.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[0.68rem] font-semibold px-2.5 py-1 rounded-full ${platform?.billing_enabled ? "bg-[hsl(158_74%_92%)] text-[hsl(158_74%_30%)]" : "bg-muted text-muted-foreground"}`}>
            {platform?.billing_enabled ? "Enabled" : "Disabled"}
          </span>
          <Switch
            checked={!!platform?.billing_enabled}
            onCheckedChange={toggleGlobalBilling}
            data-testid="toggle-global-billing"
          />
        </div>
      </div>

      {/* Danger zone — remove demo data before going live */}
      {demoSummary?.has_demo && (
        <div
          className="mb-6 border border-destructive/30 rounded-2xl bg-destructive/5 p-5 flex items-center justify-between gap-4 flex-wrap"
          data-testid="demo-danger-card"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-base">Demo data detected</div>
              <div className="text-xs text-muted-foreground mt-1 max-w-xl">
                {demoSummary.tenants} seed tenant{demoSummary.tenants !== 1 ? "s" : ""}
                {demoSummary.tenant_names?.length ? ` (${demoSummary.tenant_names.join(", ")})` : ""} —
                {" "}{demoSummary.users} users · {demoSummary.facilities} facilities · {demoSummary.tasks} tasks · {demoSummary.checklists} checklists.
                Remove them before you go live. The platform admin login and your real tenants are untouched.
              </div>
            </div>
          </div>
          <Dialog open={wipeOpen} onOpenChange={(o) => { setWipeOpen(o); if (!o) setWipeConfirm(""); }}>
            <DialogTrigger asChild>
              <Button variant="destructive" className="rounded-xl" data-testid="open-wipe-demo">
                <Trash2 className="w-4 h-4 mr-1" />Wipe demo data
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Permanently delete demo data?</DialogTitle>
                <DialogDescription>
                  This deletes {demoSummary.tenants} tenant{demoSummary.tenants !== 1 ? "s" : ""} and every record scoped to them — users, facilities, checklists, tasks, shifts, notifications, audit logs, payments. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label>Type <span className="font-mono font-semibold">WIPE</span> to confirm</Label>
                <Input
                  value={wipeConfirm}
                  onChange={(e) => setWipeConfirm(e.target.value)}
                  placeholder="WIPE"
                  data-testid="wipe-confirm-input"
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setWipeOpen(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={wipeConfirm !== "WIPE" || wiping}
                  onClick={wipeDemo}
                  data-testid="confirm-wipe-demo"
                  className="rounded-xl"
                >
                  {wiping ? "Deleting…" : "Delete everything"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tenants?.map((t) => {
          const PlanIcon = PLAN_ICONS[t.plan || "starter"] || Rocket;
          const trialDaysLeft = t.trial_ends_at
            ? Math.max(0, Math.ceil((new Date(t.trial_ends_at) - new Date()) / 86400000))
            : null;
          return (
            <div key={t.id} className="border border-border bg-card rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`tenant-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building className="w-5 h-5 text-primary" />
                </div>
                <StatusBadge tenant={t} />
              </div>
              <div className="font-display text-lg font-semibold">{t.name}</div>
              <div className="text-xs text-muted-foreground">{t.industry}</div>

              <div className="mt-4 flex items-center justify-between rounded-xl bg-muted/40 p-2 pr-3">
                <div className="flex items-center gap-2">
                  <PlanIcon className="w-3.5 h-3.5 text-primary" />
                  <span className="text-sm font-semibold capitalize">{t.plan || "starter"}</span>
                </div>
                {t.billing_status === "trialing" && trialDaysLeft !== null && (
                  <span className="text-[0.65rem] text-muted-foreground">{trialDaysLeft}d left</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3 pt-4 border-t border-border">
                <div>
                  <div className="text-[0.6rem] tracking-wider text-muted-foreground font-semibold uppercase">Users</div>
                  <div className="font-display text-xl font-black">{t.user_count || 0}</div>
                </div>
                <div>
                  <div className="text-[0.6rem] tracking-wider text-muted-foreground font-semibold uppercase">Facilities</div>
                  <div className="font-display text-xl font-black">{t.facility_count || 0}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
                <Button size="sm" variant="outline" className="rounded-xl flex-1" onClick={() => setEditing({ ...t })} data-testid={`manage-${t.id}`}>
                  <Settings className="w-3.5 h-3.5 mr-1" />Manage
                </Button>
                {t.billing_status === "locked" && (
                  <Button size="sm" variant="ghost" onClick={() => extendTrial(t, 14)}>
                    <Clock className="w-3.5 h-3.5 mr-1" />+14 days
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {(!tenants || tenants.length === 0) && (
          <div className="col-span-full border border-dashed p-12 text-center rounded-2xl">
            <Building className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <div className="font-medium">No tenants yet</div>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.name} · Billing</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Plan</Label>
                <Select value={editing.plan || "starter"} onValueChange={(v) => setEditing({ ...editing, plan: v })}>
                  <SelectTrigger data-testid="edit-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(plans || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.price_display}/mo</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div>
                  <div className="font-medium text-sm">Billable</div>
                  <div className="text-xs text-muted-foreground">Off = comped, bypasses lock & charges</div>
                </div>
                <Switch
                  checked={editing.is_billable !== false}
                  onCheckedChange={(v) => setEditing({ ...editing, is_billable: v })}
                  data-testid="edit-billable"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => extendTrial(editing, 14)}>
                  <Clock className="w-3.5 h-3.5 mr-1" />Extend trial 14 days
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Close</Button>
            <Button onClick={saveEdit} className="rounded-xl" data-testid="save-tenant">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
